/**
 * Run Website Audit button (page header).
 *
 * Mirrors components/photos/RunPhotoListButton.tsx. No file upload — the backend
 * scrapes the live site and chains off this group's latest inventory audit, so
 * the only failure ("run the Inventory Audit first") surfaces inline as the
 * server's 400 message rather than as a pre-disabled state.
 */
import React, { useState } from "react"

import { runWebsiteAudit } from "../../api/websiteAudit"
import { THEME } from "../../config/theme"

interface Props {
  dealerGroupId: number
  // Called after a successful run so the page can refresh its run list.
  onComplete: () => void
}

export default function RunWebsiteAuditButton({ dealerGroupId, onComplete }: Props) {
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<
    | { kind: "idle" }
    | { kind: "success"; message: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" })

  const handleClick = async () => {
    setResult({ kind: "idle" })
    setRunning(true)
    try {
      const run = await runWebsiteAudit(dealerGroupId)
      setResult({
        kind: "success",
        message: `Website audit generated (${run.matched} matched)`,
      })
      onComplete()
      setTimeout(() => setResult({ kind: "idle" }), 5000)
    } catch (err) {
      setResult({ kind: "error", message: err instanceof Error ? err.message : "Run failed" })
      setTimeout(() => setResult({ kind: "idle" }), 8000)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={running}
        title={running ? "Run in progress..." : undefined}
        style={{
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          background: THEME.colors.navy,
          color: THEME.colors.white,
          fontWeight: 500,
          fontSize: 14,
          border: "none",
          borderRadius: THEME.borderRadius.md,
          opacity: running ? 0.5 : 1,
          cursor: running ? "not-allowed" : "pointer",
          fontFamily: THEME.fonts.primary,
          whiteSpace: "nowrap",
          transition: "opacity 0.15s",
        }}
      >
        {running ? "Generating..." : "Generate Website Audit"}
      </button>

      {result.kind === "success" && (
        <div style={{ fontSize: 12, color: THEME.colors.semantic.success, fontWeight: 500 }}>
          {result.message}
        </div>
      )}
      {result.kind === "error" && (
        <div style={{ fontSize: 12, color: THEME.colors.semantic.danger, fontWeight: 500, maxWidth: 250 }}>
          {result.message}
        </div>
      )}
    </div>
  )
}
